import { useCallback, useMemo, useState } from 'react';

import { useColumnVisibility } from '@/features/table/hooks/useColumnVisibility';
import type { ColDef, GridApi, GridReadyEvent } from 'ag-grid-community';

import { useGridLayout } from './useGridLayout';

interface UseGridLayoutIntegrationOptions<T> {
  tableId?: string;
  showSaveLayout: boolean;
  columnDefs: ColDef<T>[];
}

export const useGridLayoutIntegration = <T>({
  tableId,
  showSaveLayout,
  columnDefs
}: UseGridLayoutIntegrationOptions<T>) => {
  const [gridApi, setGridApi] = useState<GridApi<T> | null>(null);

  const canSaveLayout = showSaveLayout && !!tableId;

  const {
    saveLayout,
    isSaving,
    savedVisibleColumnIds,
    savedColumnOrder,
    savedColumnWidths,
    isLayoutReady,
    hasSavedLayout,
    applyLayoutToGrid
  } = useGridLayout(gridApi, {
    tableId: tableId ?? '',
    enabled: canSaveLayout
  });

  const {
    visibleColumnDefs: baseVisibleColumnDefs,
    allColumns,
    visibleColumnIds,
    handleVisibilityChange
  } = useColumnVisibility<T>({
    columnDefs,
    savedVisibleColumnIds: hasSavedLayout ? savedVisibleColumnIds : null
  });

  // When we have a saved layout:
  // 1. Reorder columns based on saved order
  // 2. Remove flex and apply saved widths to column definitions
  const visibleColumnDefs = useMemo(() => {
    if (!hasSavedLayout) {
      return baseVisibleColumnDefs;
    }

    // Helper to get column ID
    const getColId = (col: ColDef<T>): string => {
      if (col.colId) return col.colId;
      if (col.field) return col.field as string;
      if (typeof col.headerName === 'string') {
        return col.headerName.toLowerCase().replace(/\s+/g, '-');
      }
      return '';
    };

    // Remove flex and apply saved widths to columns
    const columnsWithSavedWidths = baseVisibleColumnDefs.map((col) => {
      const colId = getColId(col);
      const savedWidth = savedColumnWidths?.[colId];

      // Start with a copy of the column
      let result = { ...col };

      // Remove flex if present
      if (result.flex !== null && result.flex !== undefined) {
        const { flex: _flex, ...rest } = result;
        result = rest as ColDef<T>;
      }

      // Apply saved width if available
      if (savedWidth !== undefined) {
        result = { ...result, width: savedWidth };
      }

      return result;
    });

    // Reorder columns based on saved order
    if (!savedColumnOrder || savedColumnOrder.length === 0) {
      return columnsWithSavedWidths;
    }

    const columnMap = new Map(columnsWithSavedWidths.map((col) => [getColId(col), col]));
    const orderedColumns: ColDef<T>[] = [];

    // Add columns in saved order
    for (const colId of savedColumnOrder) {
      const col = columnMap.get(colId);
      if (col) {
        orderedColumns.push(col);
        columnMap.delete(colId);
      }
    }

    // Add any remaining columns that weren't in saved order (e.g., new columns)
    for (const col of columnMap.values()) {
      orderedColumns.push(col);
    }

    return orderedColumns;
  }, [baseVisibleColumnDefs, hasSavedLayout, savedColumnOrder, savedColumnWidths]);

  // Creates a grid ready handler that applies layout and calls the provided callback
  const createGridReadyHandler = useCallback(
    (onReady?: (params: GridReadyEvent<T>) => void) => {
      return (params: GridReadyEvent<T>) => {
        setGridApi(params.api);
        applyLayoutToGrid(params.api);
        onReady?.(params);
      };
    },
    [applyLayoutToGrid]
  );

  return {
    gridApi,
    canSaveLayout,
    isLayoutReady,
    saveLayout,
    isSaving,
    visibleColumnDefs,
    allColumns,
    visibleColumnIds,
    handleVisibilityChange,
    createGridReadyHandler
  };
};
